import { useState, Fragment } from 'react';
import { useDispatch } from 'react-redux';

import Ajv from 'ajv';
import AjvErrors from 'ajv-errors';
import reduce from 'lodash/reduce';
import get from 'lodash/get';

import {
  CardHeader,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Box,
  Stack,
  Button,
  useMediaQuery,
  Divider,
  useTheme
} from '@mui/material';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import { componentMasterActions } from '../../store/reducers/component-master';
import { addComponentUISchema, addComponentSchema, addComponentTagsSchema } from './form';
import FormModal from '../../common/formModal';
import JsonEditor from './jsonEditor';

import Styles from '../../theme/commonCss/Styles';
import { useOutletContext } from 'react-router-dom';
import { palette } from '../../theme/palette';
import SelectComponent from '../../components/Select';

const ComponentStyles = {
  jsonEditorGrid: { display: 'flex', flexDirection: 'column', overflowX: 'hidden' },
  jsonEditorWrapper: {
    borderRadius: '8px',
    overflow: 'hidden',
  },
  error: { textAlign: 'left', marginTop: '8px' },
};

const AddComponent = ({
  isOpen,
  componentData,
  componentSchemaData,
  isEdit,
  changeSchemaBuilderValue = () => { },
  handleChange = () => { },
  addComponentSchemaRow = () => { },
  deleteRowItem = () => { },
  closeModal = () => { },
  servicesData,
  setcomponentData,
  setComponentSchemaData,
}: any) => {
  const themeData: any = useTheme();  
  const dispatch = useDispatch();

  const responsiveSize = useMediaQuery('(max-width:900px)');
  const ajv = new Ajv({ allErrors: true });
  AjvErrors(ajv);
  const [ajvErrors, setAjvErrors] = useState<any>({});

  const handleSubmit = (event: any) => {
    event.preventDefault();

    let jsonError = false;
    let ajvSchema: any = JSON.parse(JSON.stringify({ ...addComponentSchema }));

    if (componentSchemaData.length) {
      ajvSchema = {
        ...addComponentSchema,
        properties: {
          ...addComponentSchema.properties,
          schemas: {
            type: 'array',
            items: componentSchemaData.map((item: any) => addComponentTagsSchema),
          },
        },
        required: [...addComponentSchema.required, 'schemas'],
      };
    }

    const validate = ajv.compile(ajvSchema);

    const trimmedComponentData = reduce(
      componentData,
      (acc: any, value: any, key: any) => {
        if (['ui_schema', 'backend_schema'].includes(key)) {
          if (typeof value !== 'string') {
            acc[key] = value;
          } else {
            try {
              acc[key] = JSON.parse(value);
            } catch (error) {
              jsonError = true;
              acc[key] = value;
              setAjvErrors({
                instancePath: key,
                message: get(error, 'message', 'Something went wrong!'),
              });
            }
          }
        } else {
          acc[key] = typeof value === 'string' ? value.trim() : value;
        }
        return {
          ...acc,
        };
      },
      {}
    );

    setcomponentData(trimmedComponentData);

    const trimmedComponentTagsData = componentSchemaData.map((item: any) => {
      return {
        ...item,
        display_name: typeof item.display_name === 'string' ? item.display_name.trim() : item.display_name,
        request: typeof item.request === 'string' ? item.request.trim() : item.request,
        response: typeof item.response === 'string' ? item.response.trim() : item.response,
      };
    });

    setComponentSchemaData(trimmedComponentTagsData);

    const reqParams: any = {
      ...trimmedComponentData,
      schemas: trimmedComponentTagsData,
    };

    if (!jsonError && validate({ ...reqParams })) {
      if (isEdit) {
        dispatch(componentMasterActions.updateServiceRequest(reqParams));
      } else {
        dispatch(componentMasterActions.addServiceRequest(reqParams));
      }
      closeModal();
      setAjvErrors({});
    } else {
      if (validate.errors) {
        setAjvErrors(validate.errors[0]);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'display_name',
      headerName: 'Display Name',
      sortable: true,
      filterable: false,
      flex: 1,
      renderCell: (params: any) => {
        return (
          <TextField
            variant='outlined'
            size='small'
            value={componentSchemaData.find((schema: any) => schema.id === params.id)[params.field]}
            name={params.field}
            onChange={(e: any) => changeSchemaBuilderValue(e, params.row)}
            onKeyDown={(event: any) => {
              event.stopPropagation();
            }}
            error={
              ajvErrors.instancePath && ajvErrors.instancePath.includes(`schemas/${params.id - 1}/${params.field}`)
            }
          />
        );
      },
    },
    {
      field: 'request',
      headerName: 'Request',
      flex: 1,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <TextField
            variant='outlined'
            value={componentSchemaData.find((schema: any) => schema.id === params.id)[params.field]}
            name={params.field}
            size='small'
            onChange={(e) => changeSchemaBuilderValue(e, params.row)}
            onKeyDown={(event: any) => {
              event.stopPropagation();
            }}
            error={
              ajvErrors.instancePath && ajvErrors.instancePath.includes(`schemas/${params.id - 1}/${params.field}`)
            }
          />
        );
      },
    },
    {
      field: 'response',
      headerName: 'Response',
      flex: 1,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <TextField
            variant='outlined'
            value={componentSchemaData.find((schema: any) => schema.id === params.id)[params.field]}
            name={params.field}
            size='small'
            onChange={(e) => changeSchemaBuilderValue(e, params.row)}
            onKeyDown={(event: any) => {
              event.stopPropagation();
            }}
            error={
              ajvErrors.instancePath && ajvErrors.instancePath.includes(`schemas/${params.id - 1}/${params.field}`)
            }
          />
        );
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 80,
      sortable: false,
      filterable: false,
      align: 'center',
      renderCell: (params: any) => {
        return (
          <IconButton onClick={(e) => deleteRowItem(e, params.row)}>
            <DeleteOutlineIcon fontSize='small' fontWeight='normal' />
          </IconButton>
        );
      },
    },
  ];

  const renderFields = () => (
    <Grid
      container
      spacing={3}
      sx={{ ...Styles.gridContainer, mt: responsiveSize ? 0 : '12px' }}
      alignItems='flex-start'
      marginLeft='0px'
    >
      {addComponentUISchema.map(({ name, label, type, required }: any) => {
        if (type === 'input') {
          return (
            <Fragment key={name}>
              <Grid
                item
                xs={12}
                sm={12}
                md={4}
                sx={{
                  ...Styles.gridItem,
                  '&.MuiGrid-item': {
                    paddingTop: '24px !important',
                    pl: '0px !important',
                    justifyContent: responsiveSize ? 'flex-start' : 'flex-end',
                  },
                }}
              >
                <Typography sx={{ ...Styles.inputLabel, color: themeData.palette.text.primary }} className={required ? 'required-field' : ''}>
                  {label}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={8}
                sx={{
                  '&.MuiGrid-item': {
                    padding: responsiveSize ? '4px 0 0' : '12px 0px',
                  },
                }}
              >
                <TextField
                  margin='normal'
                  fullWidth
                  id={name}
                  onChange={(e) => {
                    handleChange(e.target.name, e.target.value);
                  }}
                  name={name}
                  autoComplete={name}
                  autoFocus
                  variant='outlined'
                  value={componentData[name]}
                  size='small'
                  helperText={ajvErrors.instancePath && ajvErrors.instancePath.includes(name) && ajvErrors.message}
                  error={ajvErrors.instancePath && ajvErrors.instancePath.includes(name)}
                  sx={{ mt: 0, mb: 0 }}
                />
              </Grid>
            </Fragment>
          );
        } else if (type === 'textarea') {
          const isError = get(ajvErrors, 'instancePath', '').includes(name);
          return (
            <Fragment key={name}>
              <Grid
                item
                xs={12}
                sm={12}
                md={4}
                sx={{
                  ...Styles.gridItem,
                  '&.MuiGrid-item': {
                    paddingTop: '24px !important',
                    pl: '0px !important',
                    justifyContent: responsiveSize ? 'flex-start' : 'flex-end',
                  },
                }}
              >
                <Typography sx={{ ...Styles.inputLabel, color: themeData.palette.text.primary }} className={required ? 'required-field' : ''}>
                  {label}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={8}
                sx={{
                  ...ComponentStyles.jsonEditorGrid,
                  '&.MuiGrid-item': {
                    padding: responsiveSize ? '4px 0 0 0' : '12px 0px',
                  },
                }}
              >
                <JsonEditor
                  code={
                    typeof componentData[name] === 'string'
                      ? componentData[name]
                      : JSON.stringify(componentData[name], undefined, 2)
                  }
                  onChange={(value: string) => {
                    handleChange(name, value);
                  }}
                  height='288px'
                  styles={{
                    ...ComponentStyles.jsonEditorWrapper,
                    border: `1px solid ${isError ? palette.error.main : themeData.palette.customBorderColor.main}`,
                  }}
                />
                {isError && (
                  <Typography variant='body2' color='error' sx={ComponentStyles.error}>
                    {get(ajvErrors, 'message', '')}
                  </Typography>
                )}
              </Grid>
            </Fragment>
          );
        } else if (type === 'select') {
          return (
            <Fragment key={name}>
              <Grid
                item
                xs={12}
                sm={12}
                md={4}
                sx={{
                  ...Styles.gridItem,
                  '&.MuiGrid-item': {
                    paddingTop: '24px !important',
                    pl: '0px !important',
                    justifyContent: responsiveSize ? 'flex-start' : 'flex-end',
                  },
                }}
              >
                <Typography sx={{ ...Styles.inputLabel, color: themeData.palette.text.primary }} className={required ? 'required-field' : ''}>
                  {label}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={8}
                sx={{
                  '&.MuiGrid-item': {
                    padding: responsiveSize ? '4px 0 0 0' : '12px 0px',
                  },
                }}
              >
                     <SelectComponent
                      value={componentData[name]}
                      options={servicesData.map((option: any) => ({ ...option, label: option.service_name, value: option.service_id }))}
                      onChange={(e :any) => {
                      handleChange(e.target.name, e.target.value);
                      }}
                      name={name}
                      error={ajvErrors.instancePath && ajvErrors.instancePath.includes(name)}
                      helperText=' Please select Service'
                    />   
              </Grid>
            </Fragment>
          );
        } else {
          return <></>;
        }
      })}
    </Grid>
  );

  return (
    <FormModal title={isEdit ? 'EDIT COMPONENT' : 'ADD NEW COMPONENT'} isOpen={isOpen} handleSubmit={handleSubmit} handleClose={closeModal}>
      {renderFields()}

      {/* <CardHeader title='Component Schema Builder' titleTypographyProps={Styles.title} sx={Styles.cardHeader} />
      <Grid container spacing={2}>
        <Grid item xs={12} textAlign={'right'}>
          <div style={{ height: '200px', width: '100%', marginTop: '10px' }}>
            <DataGrid
              rows={componentSchemaData}
              columns={columns}
              disableColumnMenu
              hideFooterPagination
              hideFooter
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay:() => (
                  <Stack height='100%' alignItems='center' justifyContent='center'>
                    Metadata not available
                  </Stack>
                )
              }}
              sx={{
                '&.MuiDataGrid-root': {
                  borderColor: 'transparent !important',
                },
                '&.MuiDataGrid-root .MuiDataGrid-withBorderColor': {
                  borderColor: 'transparent !important',
                },
                '& .MuiDataGrid-cell': {
                  backgroundColor: 'white',
                  borderColor: 'transparent',
                },
                '& .MuiDataGrid-cell:hover': {
                  backgroundColor: 'white',
                  borderColor: 'transparent',
                },
                '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                  outline: 'none !important',
                },
              }}
            />
          </div>
        </Grid>
        <Grid item xs={8} textAlign={'left'}>
          {ajvErrors.instancePath && ajvErrors.instancePath.includes('schemas') && (
            <Typography variant='body2' color='error' sx={{ textAlign: 'left', marginTop: '8px' }}>
              {ajvErrors.message}
            </Typography>
          )}
        </Grid>
        <Grid item xs={4} textAlign={'right'}>
          <Button
            variant='text'
            startIcon={<AddCircleOutlineOutlinedIcon />}
            onClick={(e) => addComponentSchemaRow(e, componentSchemaData.length)}
          >
            Add New Item
          </Button>
        </Grid>
      </Grid> */}
    </FormModal>
  );
};

export default AddComponent;
